import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { FINANCE_TABS } from '@/constants/financeTabs'
import { getCashFlowManager, type CashFlowManagerData } from '@/services/api/finance'

function fmtZar(n: number, digits = 0): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    currencyDisplay: 'symbol',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n)
}

function fmtAmt(n: number, ccy = 'ZAR'): string {
  if (ccy === 'USD') {
    return `US$ ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function Card({
  title,
  value,
  sub,
  valueColor,
}: {
  title: string
  value: string
  sub: string
  valueColor?: string
}) {
  return (
    <div
      className="bg-white"
      style={{
        borderRadius: 10,
        border: '1px solid #ECECEF',
        padding: '18px 20px',
        minHeight: 108,
        boxShadow: '0 1px 2px rgba(33, 33, 38, 0.04)',
      }}
    >
      <p
        style={{
          fontFamily: 'Lexend Deca',
          fontWeight: 400,
          fontSize: 12,
          letterSpacing: '0.01em',
          color: '#69697A',
          marginBottom: 10,
        }}
      >
        {title}
      </p>
      <p
        style={{
          fontFamily: 'Lexend Deca',
          fontWeight: 600,
          fontSize: 26,
          lineHeight: '32px',
          letterSpacing: '-0.02em',
          color: valueColor || '#212126',
          marginBottom: 8,
        }}
      >
        {value}
      </p>
      <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: 12, color: '#8B8B97' }}>
        {sub}
      </p>
    </div>
  )
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <div
      className="bg-white"
      style={{
        borderRadius: 10,
        border: '1px solid #ECECEF',
        padding: '24px 26px',
        boxShadow: '0 1px 2px rgba(33, 33, 38, 0.04)',
      }}
    >
      <h3
        style={{
          fontFamily: 'Lexend Deca',
          fontWeight: 600,
          fontSize: 15,
          letterSpacing: '-0.01em',
          color: '#212126',
          marginBottom: subtitle ? 4 : 18,
        }}
      >
        {title}
      </h3>
      {subtitle ? (
        <p
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: 13,
            lineHeight: '20px',
            color: '#69697A',
            marginBottom: 18,
          }}
        >
          {subtitle}
        </p>
      ) : null}
      {children}
    </div>
  )
}

const CASH_FLOW_POLL_MS = 60_000

const FinanceCashFlow = () => {
  const location = useLocation()
  const [data, setData] = useState<CashFlowManagerData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)

  useEffect(() => {
    let cancelled = false
    let inFlight = false

    const load = async (silent: boolean) => {
      if (cancelled || inFlight) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      inFlight = true
      try {
        if (!silent) {
          setLoading(true)
          setError(null)
        }
        const res = await getCashFlowManager()
        if (cancelled) return
        setData(res)
        setUpdatedAt(new Date())
      } catch (e: any) {
        if (!cancelled && !silent) setError(e?.message || 'Failed to load cash flow')
      } finally {
        inFlight = false
        if (!cancelled) setLoading(false)
      }
    }

    void load(false)
    const intervalId = window.setInterval(() => {
      void load(true)
    }, CASH_FLOW_POLL_MS)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void load(true)
    }
    const onFocus = () => {
      void load(true)
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  const asOfLabel = data?.asOfDate
    ? new Date(data.asOfDate + 'T12:00:00').toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              color: '#212126',
            }}
          >
            Finance
          </h1>
          <span
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Cash flow</span>
          </span>
        </div>

        <div
          className="flex items-center overflow-x-auto scrollbar-hide"
          style={{
            width: '100%',
            height: 38,
            borderRadius: 8,
            border: '1px solid #E9E9EA',
            backgroundColor: '#FFFFFF',
            padding: 4,
            gap: 0,
          }}
        >
          {FINANCE_TABS.map((tab) => {
            const isActive = location.pathname === tab.path
            return (
              <Link
                key={tab.path}
                to={tab.path}
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 12,
                  lineHeight: '20px',
                  padding: '6px 12px',
                  borderRadius: 6,
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  color: isActive ? '#FFFFFF' : '#69697A',
                  backgroundColor: isActive ? '#212126' : 'transparent',
                }}
              >
                {tab.label}
              </Link>
            )
          })}
        </div>
      </div>

      <div
        style={{
          backgroundColor: '#F6F6F7',
          borderRadius: 12,
          border: '1px solid #ECECEF',
          padding: '28px 28px 32px',
        }}
      >
        <p
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: 11,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#8B8B97',
            marginBottom: 10,
          }}
        >
          Cash flow manager
          {updatedAt
            ? ` · updated ${updatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
            : ''}
        </p>
        <h2
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 600,
            fontSize: 24,
            letterSpacing: '-0.02em',
            color: '#212126',
            marginBottom: 8,
          }}
        >
          Receivables and payables by due date
        </h2>
        <p
          style={{
            fontFamily: 'Lexend Deca',
            fontSize: 13,
            lineHeight: '20px',
            color: '#69697A',
            marginBottom: 22,
            maxWidth: 720,
          }}
        >
          Live from open Xero invoices and bills as of {asOfLabel}. Amounts in ZAR unless stated.
        </p>

        {loading && !data ? (
          <div
            className="bg-white"
            style={{ borderRadius: 10, border: '1px solid #ECECEF', padding: 28, color: '#69697A' }}
          >
            Loading cash flow…
          </div>
        ) : error ? (
          <div
            className="bg-white"
            style={{ borderRadius: 10, border: '1px solid #ECECEF', padding: 28, color: '#E93323' }}
          >
            {error}
          </div>
        ) : data ? (
          <div className="space-y-5">
            <div
              className="bg-white"
              style={{
                borderRadius: 10,
                border: '1px solid #ECECEF',
                padding: '20px 24px',
                boxShadow: '0 1px 2px rgba(33, 33, 38, 0.04)',
              }}
            >
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: 15,
                  lineHeight: '24px',
                  color: '#212126',
                }}
              >
                {data.summaryNarrative}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <Card
                title="Receivable (net of set-off)"
                value={fmtZar(data.cards.receivableNet)}
                sub={`Gross: ${fmtZar(data.cards.receivableGross, 2)}`}
              />
              <Card
                title="Overdue receivables"
                value={fmtZar(data.cards.overdueReceivable)}
                sub={`Due before ${asOfLabel}`}
                valueColor="#E93323"
              />
              <Card
                title="Payable ZAR (net of set-off)"
                value={fmtZar(data.cards.payableZarNet)}
                sub={`Gross: ${fmtZar(data.cards.payableZarGross, 2)}`}
              />
              <Card
                title="Overdue payables ZAR"
                value={fmtZar(data.cards.overduePayableZar)}
                sub={`Due before ${asOfLabel}`}
                valueColor="#E93323"
              />
              <Card
                title="Payable USD"
                value={fmtAmt(data.cards.payableUsd, 'USD')}
                sub={`Overdue: ${fmtAmt(data.cards.overduePayableUsd, 'USD')}`}
              />
              <Card
                title="Net position ZAR (receivable − payable)"
                value={fmtZar(data.cards.netPositionZar)}
                sub={
                  data.cards.setOffAmount > 0
                    ? `${data.cards.setOffLabel}: ${fmtZar(data.cards.setOffAmount, 2)}`
                    : data.cards.setOffLabel
                }
                valueColor="#13AB5B"
              />
            </div>

            <Section
              title="Cash flow by due-date bucket (ZAR)"
              subtitle={`Balances net of set-off, grouped by due date relative to ${asOfLabel}. USD shown separately above.`}
            >
              <div style={{ width: '100%', height: 280, marginBottom: 20 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.buckets}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={(v) =>
                        new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(
                          Number(v)
                        )
                      }
                      tick={{ fill: '#69697A', fontFamily: 'Lexend Deca', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={160}
                      tick={{ fill: '#212126', fontFamily: 'Lexend Deca', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [fmtZar(Number(value), 2), name]}
                    />
                    <Legend />
                    <Bar dataKey="receipts" name="Receipts" fill="#2C71F6" radius={[0, 4, 4, 0]} barSize={10} />
                    <Bar dataKey="payments" name="Payments" fill="#F59E0B" radius={[0, 4, 4, 0]} barSize={10} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Bucket', 'Receipts', 'Payments', 'Net', 'Cumulative'].map((h) => (
                        <th
                          key={h}
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 600,
                            fontSize: 12,
                            color: '#69697A',
                            textAlign: h === 'Bucket' ? 'left' : 'right',
                            padding: '10px 8px',
                            borderBottom: '1px solid #E9E9EA',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.buckets.map((b) => (
                      <tr key={b.key}>
                        <td
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontSize: 13,
                            color: '#212126',
                            padding: '10px 8px',
                            borderBottom: '1px solid #F3F4F6',
                          }}
                        >
                          {b.label}
                        </td>
                        {[b.receipts, b.payments, b.net, b.cumulative].map((v, i) => (
                          <td
                            key={i}
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: 13,
                              color: i === 2 && v < 0 ? '#E93323' : '#212126',
                              textAlign: 'right',
                              padding: '10px 8px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            {fmtAmt(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {data.setOff ? (
              <Section title={data.setOff.title} subtitle={data.setOff.note}>
                <table className="w-full" style={{ borderCollapse: 'collapse', maxWidth: 560 }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 600,
                          fontSize: 12,
                          color: '#69697A',
                          textAlign: 'left',
                          padding: '10px 8px',
                          borderBottom: '1px solid #E9E9EA',
                        }}
                      >
                        Item
                      </th>
                      <th
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 600,
                          fontSize: 12,
                          color: '#69697A',
                          textAlign: 'right',
                          padding: '10px 8px',
                          borderBottom: '1px solid #E9E9EA',
                        }}
                      >
                        Amount ZAR
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.setOff.items.map((row) => (
                      <tr key={row.item}>
                        <td
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontSize: 13,
                            fontWeight: row.item.toLowerCase().includes('net balance') ? 600 : 400,
                            color: '#212126',
                            padding: '10px 8px',
                            borderBottom: '1px solid #F3F4F6',
                          }}
                        >
                          {row.item}
                        </td>
                        <td
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontSize: 13,
                            fontWeight: row.item.toLowerCase().includes('net balance') ? 600 : 400,
                            textAlign: 'right',
                            color: '#212126',
                            padding: '10px 8px',
                            borderBottom: '1px solid #F3F4F6',
                          }}
                        >
                          {fmtAmt(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            ) : null}

            {data.dualCounterparties.length > 0 ? (
              <Section
                title="Other customers who are also suppliers"
                subtitle="Cross balances where a set-off similar to Kalinda’s could be requested."
              >
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Counterparty', 'They owe us', 'We owe them', "Net in KCG's favour", 'Status'].map(
                          (h) => (
                            <th
                              key={h}
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#69697A',
                                textAlign: h === 'Counterparty' || h === 'Status' ? 'left' : 'right',
                                padding: '10px 8px',
                                borderBottom: '1px solid #E9E9EA',
                              }}
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.dualCounterparties.map((row) => (
                        <tr key={row.counterparty}>
                          <td
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: 13,
                              color: '#212126',
                              padding: '10px 8px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            {row.counterparty}
                          </td>
                          <td
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: 13,
                              textAlign: 'right',
                              padding: '10px 8px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            {fmtAmt(row.theyOweUs)}
                          </td>
                          <td
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: 13,
                              textAlign: 'right',
                              padding: '10px 8px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            {fmtAmt(row.weOweThem)}
                          </td>
                          <td
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: 13,
                              textAlign: 'right',
                              color: row.netInKcgFavour < 0 ? '#E93323' : '#212126',
                              padding: '10px 8px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            {fmtAmt(row.netInKcgFavour)}
                          </td>
                          <td
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: 13,
                              color: row.setOffApplied ? '#13AB5B' : '#69697A',
                              padding: '10px 8px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            {row.status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            ) : null}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Section
                title="Receivable by customer"
                subtitle="Net of set-off, open balances split overdue / not yet due."
              >
                <PartyTable
                  nameHeader="Customer"
                  rows={data.receivableByCustomer}
                />
              </Section>
              <Section
                title="Payable by supplier"
                subtitle="Net of set-off, open balances split overdue / not yet due."
              >
                <PartyTable nameHeader="Supplier" rows={data.payableBySupplier} />
              </Section>
            </div>

          </div>
        ) : null}
      </div>
    </div>
  )
}

function PartyTable({
  nameHeader,
  rows,
}: {
  nameHeader: string
  rows: CashFlowManagerData['receivableByCustomer']
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {[nameHeader, 'Ccy', 'Overdue', 'Not yet due', 'Total'].map((h) => (
              <th
                key={h}
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: 12,
                  color: '#69697A',
                  textAlign: h === nameHeader || h === 'Ccy' ? 'left' : 'right',
                  padding: '10px 8px',
                  borderBottom: '1px solid #E9E9EA',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: 13,
                  color: '#69697A',
                  padding: '16px 8px',
                }}
              >
                No open balances
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={`${row.counterparty}-${row.currency}`}>
                <td
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: 13,
                    color: '#212126',
                    padding: '10px 8px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  {row.counterparty}
                </td>
                <td
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: 13,
                    color: '#69697A',
                    padding: '10px 8px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  {row.currency}
                </td>
                <td
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: 13,
                    textAlign: 'right',
                    color: row.overdue > 0 ? '#E93323' : '#212126',
                    padding: '10px 8px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  {fmtAmt(row.overdue)}
                </td>
                <td
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: 13,
                    textAlign: 'right',
                    padding: '10px 8px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  {fmtAmt(row.notYetDue)}
                </td>
                <td
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: 13,
                    fontWeight: 600,
                    textAlign: 'right',
                    padding: '10px 8px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  {fmtAmt(row.total)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

export default FinanceCashFlow
